import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, ArrowUpRight } from "lucide-react";
import { BUSINESS, LOGO_URL } from "@/data/content";

const NAV = [
  { label: "Home", href: "#home" },
  { label: "Services", href: "#services" },
  { label: "About", href: "#about" },
  { label: "Contact", href: "#contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-obsidian/85 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto max-w-[1500px] px-5 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-[72px]">
          {/* Logo */}
          <a
            href="#home"
            className="flex items-center gap-3 group"
            data-testid="logo-link"
          >
            <img
              src={LOGO_URL}
              alt="Delta Grassmasters LLC logo"
              className="h-11 w-auto object-contain"
            />
            <span className="hidden sm:block h-8 w-px bg-white/15" />
            <span className="hidden sm:block text-[10px] leading-tight tracking-[0.22em] text-brand uppercase font-semibold">
              Commercial<br />Landscaping<br />AR &amp; MS
            </span>
          </a>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-9">
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className="relative text-sm font-medium text-[#c7ccc4] hover:text-[#f2f4f0] transition-colors duration-200 group"
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-brand transition-[width] duration-300 group-hover:w-full" />
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={BUSINESS.phoneHref}
              data-testid="header-call-btn"
              className="hidden sm:inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 text-sm font-semibold text-[#f2f4f0] hover:border-brand hover:text-brand transition-colors duration-200"
            >
              <Phone className="h-4 w-4" />
              {BUSINESS.phone}
            </a>
            <a
              href="#contact"
              data-testid="header-quote-btn"
              className="hidden md:inline-flex items-center gap-1.5 bg-brand px-4 py-2.5 text-sm font-bold text-[#0b120a] hover:bg-brand-hover transition-colors duration-200 group"
            >
              Request a Quote
              <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <button
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden text-[#f2f4f0] p-2"
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden bg-obsidian border-b border-white/10"
            data-testid="mobile-nav"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={() => setOpen(false)}
                  className="font-display text-2xl font-bold text-[#f2f4f0] uppercase tracking-tight"
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </a>
              ))}
              <a
                href={BUSINESS.phoneHref}
                className="mt-2 inline-flex items-center gap-2 bg-brand px-5 py-3 font-bold text-[#0b120a] w-fit"
                data-testid="mobile-call-btn"
              >
                <Phone className="h-4 w-4" /> {BUSINESS.phone}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
